import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { ProductSummary } from './portfolio.service';

/** Current published offering document (PPM) summary — drives the managed
 *  download + acknowledgement flow on the detail page. */
export interface OfferingDocumentSummary {
  document_id: string;
  doc_type: string;
  version: number;
  acknowledged: boolean;
  /** The DRAFT legend line for counsel on the offering document of a US issuer; null otherwise. */
  legend: string | null;
}

export interface OfferingDownload {
  download_url: string;
  expires_in_seconds: number;
  document_id: string;
  doc_type: string;
  version: number;
  file_hash: string;
  acknowledged: boolean;
}

/** Extends `ProductSummary` with detail-page-only fields the catalog
 *  list endpoint doesn't bother returning (saves tab-render payload). */
export interface ProductDetail extends ProductSummary {
  prospectus_url: string | null;
  is_active: boolean;
  offering_document: OfferingDocumentSummary | null;
}

/**
 * Catalog + single-product endpoints. The catalog feeds the
 * YieldSimulatorWidget (curated trio); `getProduct(id)` powers the
 * `/client/products/{id}` detail page reached from the simulator's
 * "View product details" link.
 */
class CatalogService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listCatalog(limit = 3): Promise<ProductSummary[]> {
    const res = await this.api.get<{ items: ProductSummary[] }>(
      '/api/v1/products/catalog',
      { params: { limit } },
    );
    return res.data.items;
  }

  async getProduct(productId: string): Promise<ProductDetail> {
    const res = await this.api.get<ProductDetail>(
      `/api/v1/products/${productId}`,
    );
    return res.data;
  }

  /** Presigned GET for the current offering document. Server-side this also
   *  writes the delivery-evidence audit row, so call it only on an explicit
   *  user action (button click), never on page load. */
  async getOfferingDownload(productId: string): Promise<OfferingDownload> {
    const res = await this.api.get<OfferingDownload>(
      `/api/v1/products/${productId}/offering-document`,
    );
    return res.data;
  }

  /** Record the investor's acknowledgement of the current version. */
  async acknowledgeOffering(productId: string): Promise<void> {
    await this.api.post(`/api/v1/products/${productId}/offering-document/acknowledge`);
  }
}

export default new CatalogService();
